<template>
  <div class="software-manager">
    <div class="flex items-center justify-between mb-4">
      <div>
        <h2 class="text-lg font-bold">软件分享</h2>
        <p class="text-xs text-neutral-500 mt-1">
          仅管理员可发布：支持网盘链接或站内附件。前台页面会提示“仅分享使用”
        </p>
      </div>
      <button
        @click="openEdit(null)"
        class="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
      >
        <i class="fa fa-plus mr-1"></i>新增软件
      </button>
    </div>

    <div v-if="loading" class="text-center py-10">
      <i class="fa fa-spinner fa-spin text-primary text-2xl"></i>
    </div>

    <div v-else-if="list.length === 0" class="text-center py-16 border rounded-xl">
      <p class="text-neutral-500">还没有软件，点右上角新增</p>
    </div>

    <div
      v-for="s in list"
      :key="s.id"
      class="border border-neutral-200 rounded-xl p-4 mb-3 flex items-start gap-4 bg-white"
    >
      <div
        class="w-12 h-12 rounded-xl bg-neutral-100 flex items-center justify-center overflow-hidden flex-shrink-0"
      >
        <img v-if="s.cover" :src="s.cover" class="w-full h-full object-cover" />
        <i v-else class="fa fa-cube text-neutral-400"></i>
      </div>

      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-2 flex-wrap">
          <span class="font-medium">{{ s.title }}</span>
          <span
            class="px-2 py-0.5 rounded text-xs"
            :class="
              s.status === 1
                ? 'bg-green-50 text-green-700'
                : 'bg-neutral-100 text-neutral-500'
            "
            >{{ s.status === 1 ? "已上架" : "已下架" }}</span
          >
          <span class="px-2 py-0.5 rounded text-xs" :class="examClass(s)">{{
            examText(s)
          }}</span>
          <span
            v-if="s.uid"
            class="px-2 py-0.5 rounded text-xs bg-neutral-100 text-neutral-500"
            >用户投稿</span
          >
          <span class="px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700">{{
            s.shareType === "file" ? "站内附件" : s.panName || "网盘"
          }}</span>
          <span v-if="s.category" class="text-xs text-neutral-400">{{
            s.category
          }}</span>
          <span class="text-xs text-neutral-400"
            >{{ s.downloadCount || 0 }} 次下载</span
          >
        </div>
        <p class="text-sm text-neutral-500 mt-1 line-clamp-1">
          {{ s.intro || "暂无介绍" }}
        </p>
        <p class="text-xs text-neutral-400 mt-1 truncate">
          <template v-if="s.shareType === 'file'">{{
            s.fileName || s.fileUrl
          }}</template>
          <template v-else
            >{{ s.panUrl
            }}<span v-if="s.panCode" class="ml-2"
              >提取码：{{ s.panCode }}</span
            ></template
          >
        </p>
      </div>

      <div class="flex items-center gap-1 flex-shrink-0">
        <button
          v-if="s.examStatus === 0 || s.examStatus === 2"
          @click="exam(s, 1)"
          class="px-3 h-8 rounded border border-green-200 text-green-600 hover:bg-green-50 text-xs"
        >
          通过
        </button>
        <button
          v-if="s.examStatus === 0 || s.examStatus === 2"
          @click="exam(s, 2)"
          class="px-3 h-8 rounded border border-amber-200 text-amber-600 hover:bg-amber-50 text-xs"
        >
          驳回
        </button>
        <button
          @click="toggleStatus(s)"
          class="px-3 h-8 rounded border border-neutral-300 text-neutral-600 hover:border-primary hover:text-primary text-xs"
        >
          {{ s.status === 1 ? "下架" : "上架" }}
        </button>
        <button
          @click="openEdit(s)"
          class="px-3 h-8 rounded border border-neutral-300 text-neutral-600 hover:border-primary hover:text-primary text-xs"
        >
          编辑
        </button>
        <button
          @click="remove(s)"
          class="px-3 h-8 rounded border border-red-200 text-red-500 hover:bg-red-50 text-xs"
        >
          删除
        </button>
      </div>
    </div>

    <!-- 编辑弹窗 -->
    <div
      v-if="editing"
      class="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      @click.self="editing = false"
    >
      <div
        class="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <div
          class="px-6 py-4 border-b border-neutral-100 flex items-center justify-between sticky top-0 bg-white"
        >
          <h3 class="font-bold">{{ form.id ? "编辑软件" : "新增软件" }}</h3>
          <button @click="editing = false" class="text-neutral-400 hover:text-neutral-600">
            <i class="fa fa-close"></i>
          </button>
        </div>

        <div class="p-6 space-y-4">
          <div>
            <label class="block text-sm font-medium mb-1"
              >软件名称 <span class="text-red-500">*</span></label
            >
            <input
              v-model="form.title"
              class="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
              placeholder="如：班级管家 2.4"
            />
          </div>

          <div>
            <label class="block text-sm font-medium mb-1">简介</label>
            <input
              v-model="form.intro"
              class="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
              placeholder="一句话说明这个软件能做什么"
            />
          </div>

          <div>
            <label class="block text-sm font-medium mb-1">封面</label>
            <div class="flex items-center gap-3">
              <div
                class="w-14 h-14 rounded-lg bg-neutral-100 flex items-center justify-center overflow-hidden flex-shrink-0"
              >
                <img
                  v-if="form.cover"
                  :src="form.cover"
                  class="w-full h-full object-cover"
                />
                <i v-else class="fa fa-image text-neutral-400"></i>
              </div>
              <label
                class="px-3 py-1.5 border border-neutral-300 rounded-lg text-sm cursor-pointer hover:border-primary hover:text-primary"
              >
                {{ coverUploading ? "上传中…" : "选择图片" }}
                <input
                  type="file"
                  accept="image/*"
                  class="hidden"
                  :disabled="coverUploading"
                  @change="uploadCover"
                />
              </label>
              <button
                v-if="form.cover"
                @click="form.cover = ''"
                class="text-xs text-neutral-400 hover:text-red-500"
              >
                移除
              </button>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-sm font-medium mb-1">分类</label>
              <select
                v-model="form.category"
                class="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
              >
                <option value="">未分类</option>
                <option v-for="c in categories" :key="c" :value="c">{{ c }}</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium mb-1">版本</label>
              <input
                v-model="form.version"
                class="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
                placeholder="如 2.4.1"
              />
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium mb-1">适用平台</label>
            <div class="flex flex-wrap gap-3 text-sm">
              <label
                v-for="p in platformOptions"
                :key="p.value"
                class="flex items-center gap-1 cursor-pointer"
              >
                <input
                  type="checkbox"
                  :value="p.value"
                  v-model="checkedPlatforms"
                  class="accent-primary"
                />
                <span>{{ p.label }}</span>
              </label>
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium mb-1">分享方式</label>
            <div class="flex gap-4 text-sm">
              <label class="flex items-center gap-1 cursor-pointer">
                <input
                  type="radio"
                  value="pan"
                  v-model="form.shareType"
                  class="accent-primary"
                />
                <span>网盘链接</span>
              </label>
              <label class="flex items-center gap-1 cursor-pointer">
                <input
                  type="radio"
                  value="file"
                  v-model="form.shareType"
                  class="accent-primary"
                />
                <span>站内附件</span>
              </label>
            </div>
          </div>

          <!-- 网盘 -->
          <template v-if="form.shareType === 'pan'">
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-sm font-medium mb-1">网盘平台</label>
                <select
                  v-model="form.panName"
                  class="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
                >
                  <option v-for="p in panOptions" :key="p" :value="p">{{ p }}</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium mb-1">提取码</label>
                <input
                  v-model="form.panCode"
                  class="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
                  placeholder="没有就留空"
                />
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium mb-1"
                >分享链接 <span class="text-red-500">*</span></label
              >
              <input
                v-model="form.panUrl"
                class="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
                placeholder="https://pan.baidu.com/s/..."
              />
            </div>
            <div>
              <label class="block text-sm font-medium mb-1">大小说明</label>
              <input
                v-model="form.sizeText"
                class="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
                placeholder="手填，如 256MB"
              />
            </div>
          </template>

          <!-- 站内附件 -->
          <template v-else>
            <div>
              <label class="block text-sm font-medium mb-1"
                >附件 <span class="text-red-500">*</span></label
              >
              <div class="flex items-center gap-3">
                <label
                  class="px-3 py-1.5 border border-neutral-300 rounded-lg text-sm cursor-pointer hover:border-primary hover:text-primary"
                >
                  {{ fileUploading ? "上传中…" : "选择文件" }}
                  <input
                    type="file"
                    class="hidden"
                    :disabled="fileUploading"
                    @change="uploadFile"
                  />
                </label>
                <span v-if="form.fileName" class="text-xs text-neutral-500 truncate">
                  {{ form.fileName
                  }}<span v-if="form.fileSize" class="ml-1"
                    >({{ formatSize(form.fileSize) }})</span
                  >
                </span>
              </div>
              <p class="text-xs text-neutral-400 mt-1">
                受「上传设置」的大小限制约束，过大的软件建议用网盘链接
              </p>
            </div>
          </template>

          <div class="flex items-center gap-4">
            <div class="flex-1">
              <label class="block text-sm font-medium mb-1">排序</label>
              <input
                v-model.number="form.sortNum"
                type="number"
                class="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
              />
            </div>
            <div class="flex-1">
              <label class="block text-sm font-medium mb-1">状态</label>
              <select
                v-model.number="form.status"
                class="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
              >
                <option :value="1">上架</option>
                <option :value="0">下架</option>
              </select>
            </div>
          </div>
        </div>

        <div class="px-6 py-4 border-t border-neutral-100 flex justify-end gap-2">
          <button
            @click="editing = false"
            class="px-4 py-2 border border-neutral-300 rounded-lg text-sm"
          >
            取消
          </button>
          <button
            @click="save"
            :disabled="saving"
            class="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-60"
          >
            {{ saving ? "保存中…" : "保存" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from "element-plus";

export default {
  name: "SoftwareManager",
  data() {
    return {
      list: [],
      loading: false,
      saving: false,
      editing: false,
      coverUploading: false,
      fileUploading: false,
      checkedPlatforms: [],
      categories: ["教学工具", "办公效率", "素材资源", "系统工具", "其他"],
      platformOptions: [
        { value: "windows", label: "Windows" },
        { value: "mac", label: "macOS" },
        { value: "android", label: "安卓" },
        { value: "ios", label: "iOS" },
        { value: "web", label: "网页" },
      ],
      panOptions: ["百度网盘", "阿里云盘", "夸克网盘", "123云盘", "腾讯微云", "其他"],
      form: this.emptyForm(),
    };
  },
  created() {
    this.getList();
  },
  methods: {
    emptyForm() {
      return {
        id: "",
        title: "",
        intro: "",
        cover: "",
        category: "",
        platform: "",
        version: "",
        sizeText: "",
        shareType: "pan",
        panName: "百度网盘",
        panUrl: "",
        panCode: "",
        fileUrl: "",
        fileName: "",
        fileSize: null,
        sortNum: 0,
        status: 1,
      };
    },
    platformLabel(v) {
      const p = this.platformOptions.find((x) => x.value === v);
      return p ? p.label : v;
    },
    formatSize(bytes) {
      if (!bytes) return "";
      const k = 1024;
      const sizes = ["B", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },
    ymd() {
      const d = new Date();
      return (
        d.getFullYear() +
        "/" +
        String(d.getMonth() + 1).padStart(2, "0") +
        "/" +
        String(d.getDate()).padStart(2, "0")
      );
    },
    examText(s) {
      if (s.examStatus === 0) return "待审核";
      if (s.examStatus === 2) return "已驳回";
      return "已通过";
    },
    examClass(s) {
      if (s.examStatus === 0) return "bg-amber-50 text-amber-600";
      if (s.examStatus === 2) return "bg-red-50 text-red-500";
      return "bg-green-50 text-green-700";
    },
    /** 审核：act 1 通过 / 2 驳回（驳回必须填反馈） */
    async exam(s, act) {
      let info = "";
      try {
        if (act === 2) {
          const { value } = await ElMessageBox.prompt(
            "请写出需要修改的地方，作者会收到这条反馈",
            "驳回：" + s.title,
            {
              confirmButtonText: "确定驳回",
              cancelButtonText: "取消",
              inputType: "textarea",
              inputValidator: (v) => (v && v.trim() ? true : "请填写反馈建议"),
            }
          );
          info = (value || "").trim();
        } else {
          const { value } = await ElMessageBox.prompt(
            "可以补充通过意见（可留空）",
            "通过：" + s.title,
            { confirmButtonText: "通过", cancelButtonText: "取消" }
          );
          info = (value || "").trim();
        }
      } catch (e) {
        return;
      }
      try {
        const res = await this.$axios({
          url: "software/admin/exam",
          method: "POST",
          data: {
            data: await this.$aesEncrypt({ id: s.id, examStatus: act, examInfo: info }),
          },
        });
        if (res.code === 200) {
          ElMessage.success(act === 1 ? "已通过并上架" : "已驳回并通知作者");
          this.getList();
        } else {
          ElMessage.error(res.msg || "操作失败");
        }
      } catch (e) {
        ElMessage.error("操作失败");
      }
    },
    getList() {
      this.loading = true;
      this.$axios({ url: "software/admin/list", method: "POST" })
        .then((res) => {
          if (res.code === 200) {
            this.list = res.data || [];
          }
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
    openEdit(item) {
      this.form = item ? { ...this.emptyForm(), ...item } : this.emptyForm();
      this.checkedPlatforms = this.form.platform
        ? this.form.platform.split(",").filter(Boolean)
        : [];
      this.editing = true;
    },
    async uploadCover(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = "";
      if (!file) return;
      this.coverUploading = true;
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("UploadType", "SingleImage");
        fd.append("businessType", "software/" + this.ymd());
        const res = await this.$axios({
          url: "upload",
          method: "POST",
          data: fd,
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (res.code === 200) {
          this.form.cover = res.data.uploads.files[0].o.fullPath;
          ElMessage.success("封面上传成功");
        } else {
          ElMessage.error(res.message || res.msg || "封面上传失败");
        }
      } catch (err) {
        ElMessage.error("封面上传失败");
      } finally {
        this.coverUploading = false;
      }
    },
    async uploadFile(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = "";
      if (!file) return;
      this.fileUploading = true;
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("UploadType", "Files");
        fd.append("businessType", "software/" + this.ymd());
        const res = await this.$axios({
          url: "upload",
          method: "POST",
          data: fd,
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (res.code === 200) {
          this.form.fileUrl = res.data.uploads.files[0].o.fullPath;
          this.form.fileName = file.name;
          this.form.fileSize = file.size;
          ElMessage.success("附件上传成功");
        } else {
          ElMessage.error(res.message || res.msg || "附件上传失败");
        }
      } catch (err) {
        ElMessage.error("附件上传失败");
      } finally {
        this.fileUploading = false;
      }
    },
    async save() {
      if (!this.form.title || !this.form.title.trim()) {
        ElMessage.warning("请填写软件名称");
        return;
      }
      if (this.form.shareType === "pan") {
        if (!this.form.panUrl || !this.form.panUrl.trim()) {
          ElMessage.warning("请填写网盘分享链接");
          return;
        }
      } else if (!this.form.fileUrl) {
        ElMessage.warning("请上传附件");
        return;
      }
      this.form.platform = this.checkedPlatforms.join(",");
      this.saving = true;
      try {
        const data = await this.$aesEncrypt(this.form);
        const res = await this.$axios({
          url: "software",
          method: "PUT",
          data: { data },
        });
        if (res.code === 200) {
          ElMessage.success("保存成功");
          this.editing = false;
          this.getList();
        } else {
          ElMessage.error(res.msg || "保存失败");
        }
      } catch (err) {
        ElMessage.error("保存失败");
      } finally {
        this.saving = false;
      }
    },
    async toggleStatus(item) {
      const next = item.status === 1 ? 0 : 1;
      try {
        const payload = { ...item, status: next };
        delete payload.platformList;
        const data = await this.$aesEncrypt(payload);
        const res = await this.$axios({
          url: "software",
          method: "PUT",
          data: { data },
        });
        if (res.code === 200) {
          ElMessage.success(next === 1 ? "已上架" : "已下架");
          this.getList();
        }
      } catch (err) {
        ElMessage.error("操作失败");
      }
    },
    async remove(item) {
      if (!window.confirm(`确定删除「${item.title}」吗？`)) return;
      try {
        const res = await this.$axios({
          url: "software/" + item.id,
          method: "DELETE",
        });
        if (res.code === 200) {
          ElMessage.success("删除成功");
          this.getList();
        }
      } catch (err) {
        ElMessage.error("删除失败");
      }
    },
  },
};
</script>
